import uuid from 'uuid/v4';

import { getLogger } from '@confluence/logger';
import { containsSessionExpiredError, containsNetworkError } from '@confluence/network';

import type { ExperienceState } from './Experience.ts';
import { Experience } from './Experience.ts';
import type { ExperienceAttributes, ExperienceEvent } from './ExperienceEvent.ts';
import { getFreezeTracker } from './FreezeTracker.ts';
import { hasHealthyRunningEditPageExperience } from './createAndEditPageExperience.ts';
import { hasHealthyRunningViewPageExperience } from './viewPageSessionExperience.ts';
import {
	EDIT_PAGE_EXPERIENCE,
	LIVE_PAGE_SESSION_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
} from './ExperienceName.ts';

export type ExperienceEventSubscriber = (event: ExperienceEvent) => void;
export type ExperienceEventListenerUnsubscribe = () => void;
export type CollectFn = (events: ExperienceEvent[], experience: Experience) => void;

export type StartProps = {
	name: string;
	id?: string;
	timeout?: number;
	startTime?: number;
	attributes?: ExperienceAttributes;
	onSuccess?: () => void;
	onFailure?: () => void;
	onAbort?: () => void;
	collect?: CollectFn;
};

export type SucceedOptions = {
	name: string;
	attributes?: ExperienceAttributes;
	publishDebugPoints?: boolean;
};

export type FailOptions = {
	name?: string;
	error: Error;
	attributes?: ExperienceAttributes;
};

export type AbortOptions = {
	name?: string;
	reason: string;
	attributes?: ExperienceAttributes;
	checkForTimeout?: boolean;
	publishDebugPoints?: boolean;
};

export type StopOnErrorOptions = {
	name: string;
	error: Error;
	attributes?: ExperienceAttributes;
};

type UpdateAttributesOptions = {
	name: string;
	attributes: ExperienceAttributes;
};

type AttachDebugPointOptions = {
	name: string;
	message: string;
	attributes?: ExperienceAttributes;
};

export interface ExperienceTrackerAPI {
	start(options: StartProps): void;
	succeed(options: SucceedOptions): void;
	fail(options: FailOptions): void;
	abort(options: AbortOptions): void;

	/**
	 * Fails or aborts an experience with a specific `name` because of a specific
	 * `error` based on product-specific logic. For example, if the specified
	 * `error` describes a network error or a user error (these are internal
	 * implementation details so do not rely that any particular classification of
	 * errors is really implemented), `ExperienceStop` may abort the experience with
	 * the specified `name` rather than fail it because the errors in question are
	 * not product failures.
	 */
	stopOnError(options: StopOnErrorOptions): void;

	subscribe(subscriber: ExperienceEventSubscriber): ExperienceEventListenerUnsubscribe;

	getExperienceState(experienceName: string): ExperienceState | null;

	/**
	 * Attributes that are set at the start of the actively running experience or
	 * using this updateAttributes method will be set on all subsequent experience events.
	 *
	 * Tip: If you're updating a specific contentId's experience, you may want to validate that
	 *      the experience's getExperienceState('name')?.attributes?.contentId matches the contentId when
	 *      you're setting the attribute.
	 *
	 * Q: When should you use updateAttributes?
	 * A: If you do not have easy access to setting an attribute at the start of an experience,
	 *    it may make sense to call updateAttributes on the actively running experience to ensure that
	 *    all subsequent experience events have that attribute.
	 *    This is helpful to ensure that if your experience fails in an unexpected way, you guarantee
	 *    that all experience fails/aborts will have the attributes you expect.

	 */
	updateAttributes(options: UpdateAttributesOptions): void;

	/**
	 * Attach a debug point to the experience. A debug point is a signifier of a specific application state
	 * or point reached in a code flow. This will be included in the experience events for failures.
	 * Especially useful for compound / long running experiences, which can timeout, to track where the
	 * application logic reached prior to the timeout.
	 *
	 * @param options - The options for attaching a debug point.
	 */
	attachDebugPoint(options: AttachDebugPointOptions): void;
}

function getExperienceStopReason(error: Error): string | undefined {
	// message text check is a stop-gap measure due to re-packaging of errors. refactoring is underway.
	if (containsSessionExpiredError(error)) {
		return `Aborted because of session expiration error: ${error.toString()}`;
	}

	if (containsNetworkError(error)) {
		return `Aborted because of network error: ${error.toString()}`;
	}

	return undefined;
}

export class ExperienceTracker implements ExperienceTrackerAPI {
	private experiences: { [name: string]: Experience } = {};
	private subscribers: ExperienceEventSubscriber[] = [];
	private logger = getLogger('ExperienceTracker');

	constructor() {
		getFreezeTracker().start();
		if (typeof window !== 'undefined') {
			window.addEventListener('pagehide', () => {
				const livePageEditExperienceState = this.getExperienceState(EDIT_PAGE_EXPERIENCE);
				const livePageSessionExperienceState = this.getExperienceState(
					LIVE_PAGE_SESSION_EXPERIENCE,
				);
				const experiencesToSucceed = [
					{
						name: EDIT_PAGE_EXPERIENCE,
						successCondition:
							livePageEditExperienceState &&
							!livePageEditExperienceState.hasStopped &&
							livePageEditExperienceState.attributes?.isLivePage,
					},
					{
						name: EDIT_PAGE_EXPERIENCE,
						successCondition: hasHealthyRunningEditPageExperience(),
					},
					{
						name: VIEW_PAGE_SESSION_EXPERIENCE,
						successCondition: hasHealthyRunningViewPageExperience(),
					},
					{
						name: LIVE_PAGE_SESSION_EXPERIENCE,
						successCondition:
							livePageSessionExperienceState && !livePageSessionExperienceState.hasStopped,
					},
				];

				experiencesToSucceed.forEach(({ name, successCondition }) => {
					if (successCondition) {
						this.succeed({ name });
					}
				});

				// Abort the rest of our experiences.
				this.abort({ reason: 'Window unloading' });
			});
		}
	}

	start({
		name,
		id = uuid(),
		timeout,
		startTime,
		attributes,
		onSuccess,
		onFailure,
		onAbort,
		collect,
	}: StartProps): void {
		const current = this.experiences[name];

		// Do not restart an experience that has the same id and is still running
		if (current && current.id === id && !current.hasStopped) return;

		// Before replacing a current experience, abort it to ensure it is cleaned up.
		if (current && !current.hasStopped) {
			current.abort({
				reason: 'Aborted because the same experience was started with a new id',
				attributes: {
					newTaskId: id,
				},
			});
		}

		const experience = new Experience({
			name,
			id,
			timeout,
			startTime,
			attributes,
			onSuccess,
			onFailure,
			onAbort,
			onStart: (startEvent) => {
				this.emit(startEvent);

				let disposeCollectSubscription = () => {};
				if (collect) {
					const events: ExperienceEvent[] = [];
					disposeCollectSubscription = this.subscribe((event) => {
						// Ignore already running experiences that started before this
						// experience started
						if (
							event.action === 'taskStart' ||
							events.some(
								(previousEvent) =>
									previousEvent.name === event.name && previousEvent.action === 'taskStart',
							)
						) {
							events.push(event);
							collect(events, experience);
						}
					});
				}

				return (stopEvent) => {
					disposeCollectSubscription();
					this.emit(stopEvent);
				};
			},
		});

		this.experiences[name] = experience;
	}

	succeed({ name, attributes, publishDebugPoints }: SucceedOptions): void {
		const current = this.experiences[name];
		if (!current) return;

		current.succeed(attributes, publishDebugPoints);
	}

	fail({ name, error, attributes }: FailOptions): void {
		Object.values(this.experiences).forEach((current) => {
			if (name != null && current.name !== name) return;

			current.fail({ error, attributes });
		});
	}

	abort({ name, reason, attributes, checkForTimeout, publishDebugPoints }: AbortOptions): void {
		Object.values(this.experiences).forEach((experience) => {
			if (name != null && experience.name !== name) return;

			experience.abort({ reason, checkForTimeout, attributes, publishDebugPoints });
		});
	}

	stopOnError({ error, name, attributes }: StopOnErrorOptions): void {
		const reason = getExperienceStopReason(error);

		if (reason) {
			this.abort({
				name,
				reason,
				checkForTimeout: false,
				attributes,
			});
			return;
		}

		this.fail({ name, error, attributes });
	}

	subscribe(subscriber: ExperienceEventSubscriber): () => void {
		if (typeof subscriber !== 'function') {
			throw new Error('Subscriber must be a function');
		}
		this.subscribers.push(subscriber);
		return () => {
			this.subscribers = this.subscribers.filter((x) => x !== subscriber);
		};
	}

	getExperienceState(experienceName: string): ExperienceState | null {
		return this.experiences[experienceName] ? this.experiences[experienceName].getState() : null;
	}

	updateAttributes({ name, attributes }: UpdateAttributesOptions): void {
		const current = this.experiences[name];
		if (!current) return;

		current.updateAttributes(attributes);
	}

	attachDebugPoint({ name, message, attributes }: AttachDebugPointOptions): void {
		const current = this.experiences[name];
		if (!current) return;
		current.attachDebugPoint(message, attributes);
	}

	private emit(event: ExperienceEvent) {
		this.logger.debug`${event.action} ${event}`;
		this.subscribers.forEach((subscriber) => {
			try {
				subscriber(event);
			} catch (e) {
				this.logger
					.error`Error occurred in ExperienceTracker subscriber ${e} when handling event ${event}`;
			}
		});
	}
}
